import { z } from "zod";
import { competenciaSchema, dataSchema } from "./competencia";
import { notaDoLancamentoSchema } from "./nota";

export const TIPOS_FINANCEIROS = ["ENTRADA", "SAIDA"] as const;
export type TipoFinanceiro = (typeof TIPOS_FINANCEIROS)[number];
export const NATUREZAS = ["FIXO", "VARIAVEL"] as const;
export type NaturezaDoCusto = (typeof NATUREZAS)[number];
export const FORMAS_DE_PAGAMENTO = ["PIX", "BOLETO", "CARTAO", "DINHEIRO", "TRANSFERENCIA", "OUTRA"] as const;
export type FormaDePagamento = (typeof FORMAS_DE_PAGAMENTO)[number];
export const ORIGENS_DO_LANCAMENTO = ["MANUAL", "RECORRENTE", "COMPRA", "RECEBIMENTO", "NOTA_FISCAL"] as const;
export type OrigemDoLancamento = (typeof ORIGENS_DO_LANCAMENTO)[number];

export const FORMA_DE_PAGAMENTO_LABELS: Record<FormaDePagamento, string> = {
  PIX: "Pix",
  BOLETO: "Boleto",
  CARTAO: "Cartão",
  DINHEIRO: "Dinheiro",
  TRANSFERENCIA: "Transferência",
  OUTRA: "Outra",
};

export const ORIGEM_LABELS: Record<OrigemDoLancamento, string> = {
  MANUAL: "Manual",
  RECORRENTE: "Custo fixo",
  COMPRA: "Compra de estoque",
  RECEBIMENTO: "Recebimento",
  NOTA_FISCAL: "Nota fiscal",
};

export const NATUREZA_LABELS: Record<NaturezaDoCusto, string> = { FIXO: "Fixo", VARIAVEL: "Variável" };

export const lancamentoCriarSchema = z.object({
  tipo: z.enum(TIPOS_FINANCEIROS),
  descricao: z.string().trim().min(2, "Descreva o lançamento").max(200),
  valorCents: z.number().int().positive("Informe o valor"),
  competencia: competenciaSchema,
  categoriaId: z.string().min(1, "Escolha a categoria"),
  vencimento: dataSchema.nullable().optional(),
  pagoEm: dataSchema.nullable().optional(),
  formaDePagamento: z.enum(FORMAS_DE_PAGAMENTO).nullable().optional(),
  partnerId: z.string().min(1).nullable().optional(),
  observacao: z.string().trim().max(1000).nullable().optional(),
  /** Nota fiscal registrada junto, já ligada ao lançamento (saída → recebida, entrada → emitida). */
  nota: notaDoLancamentoSchema.optional(),
});
export type LancamentoCriarInput = z.infer<typeof lancamentoCriarSchema>;

export const lancamentoEditarSchema = lancamentoCriarSchema.omit({ tipo: true, nota: true }).extend({ id: z.string().min(1) });
export type LancamentoEditarInput = z.infer<typeof lancamentoEditarSchema>;

export const lancamentoMarcarPagoSchema = z.object({
  id: z.string().min(1),
  pagoEm: dataSchema,
  formaDePagamento: z.enum(FORMAS_DE_PAGAMENTO).nullable().optional(),
});

export const lancamentoEstornarSchema = z.object({
  id: z.string().min(1),
  motivo: z.string().trim().max(500).optional(),
});

export type SituacaoDoLancamento = "PAGO" | "VENCIDO" | "PREVISTO" | "ESTORNADO";

export const SITUACAO_LABELS: Record<SituacaoDoLancamento, string> = {
  PAGO: "Pago",
  VENCIDO: "Vencido",
  PREVISTO: "Previsto",
  ESTORNADO: "Estornado",
};

/** Datas em "AAAA-MM-DD": comparar o texto basta. */
export function situacaoDoLancamento(
  l: { pagoEm: string | null; vencimento: string | null; estornado: boolean },
  hoje: string,
): SituacaoDoLancamento {
  if (l.estornado) return "ESTORNADO";
  if (l.pagoEm) return "PAGO";
  if (l.vencimento && l.vencimento < hoje) return "VENCIDO";
  return "PREVISTO";
}
